import type { AxiosInstance } from 'axios';
import { action, computed, makeAutoObservable, runInAction } from 'mobx';
import {NotificationsStore} from './NotificationsStore';
import { IAnalyticsStore } from 'stores/AnalyticsStore';
import { AxiosResponseHelper } from 'utilities/AxiosResponseHelper';

// types
import { IResult } from 'services/Result';
import { Invite, InviteUserResponse } from 'types/models/Invite';
import { IOrganizationsStore } from './OrganizationsStore';

export interface ISendInvite {
  email: string;
  roleId: string;
  roleName: string;
  organizationId: string;
  firstName?: string;
  lastName?: string;
}

interface IInviteStore {
  invites: Invite[];
  notAbandodedInvites: Invite[];
  isLoaded: boolean;
  isError: boolean;
  error: { title: string; message: string };

  sendInvite(apiInstance: AxiosInstance, content: ISendInvite): Promise<boolean>;
  resend(apiInstance: AxiosInstance, invite: Invite): Promise<boolean>;
  cancel(apiInstance: AxiosInstance, invite: Invite): Promise<boolean>;
}

export class InviteStore implements IInviteStore {
  invites: Invite[] = [];
  @computed get notAbandodedInvites() {
    return this.invites.filter((i) => i.status.toUpperCase() != 'abandoned'.toUpperCase());
  }
  isLoaded: boolean = false;
  isError: boolean = false;
  error: { title: string; message: string } = { title: '', message: '' };

  _notifications_store: NotificationsStore;
  _organizationsStore: IOrganizationsStore;
  _analytics_store: IAnalyticsStore;

  constructor(notifications_store: NotificationsStore, organizationsStore: IOrganizationsStore, analyticsStore: IAnalyticsStore) {
    this._notifications_store = notifications_store;
    this._organizationsStore = organizationsStore;
    this._analytics_store = analyticsStore;
    makeAutoObservable(this);
  }
  @action
  public async sendInvite(apiInstance: AxiosInstance, content: ISendInvite): Promise<boolean> {
    const response = await apiInstance.post<IResult<InviteUserResponse>>('api/invites', content);
    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      await this.showInviteUserResponse(apiInstance, response.data.data, content);
      this._analytics_store.sendInviteEvent(`Invite sent to email ${content.email}`)
      return true;
    }
    let errorMsg = response.data?.[0]?.message || `Failed to send the invitation to ${content.email}`
    AxiosResponseHelper.ReportErrors(this._analytics_store, errorMsg, response.data)
    this._notifications_store.addToast(
      'Error',
      errorMsg,
      'error'
    );
    return false;
  }

  public async resend(apiInstance: AxiosInstance, invite: Invite): Promise<boolean> {
    const response = await apiInstance.put<IResult<InviteUserResponse>>('api/invites/' + invite.id + '/resend', {});
    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      await this.showInviteUserResponse(apiInstance, response.data.data, null);
      return true;
    }
    let errorMsg = `Failed to resend the invitation to ${invite.email}`
    AxiosResponseHelper.ReportErrors(this._analytics_store, errorMsg, response.data)
    this._notifications_store.addToast(
      'Error',
      errorMsg,
      'error'
    );
    return false;
  }

  public async cancel(apiInstance: AxiosInstance, invite: Invite) {
    const response = await apiInstance.delete('api/invites/' + invite.id);
    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      this._analytics_store.sendCancelInviteEvent(`Cancelled invte for email ${invite.email}`)
      return true;
    }
    let errorMsg = 'Failed to delete the invitation'
    AxiosResponseHelper.ReportErrors(this._analytics_store, errorMsg, response.data)
    this._notifications_store.addToast(
      'Error',
      errorMsg,
      'error'
    );
    return false;
  }

  private async showInviteUserResponse(apiInstance: AxiosInstance, response: InviteUserResponse | undefined, invite: ISendInvite | null) {
    if (!!!response) {
      return;
    }

    if (!!!this._organizationsStore.organizations) {
      await this._organizationsStore.getOrganizations(apiInstance);
    }

    const org = this._organizationsStore.organizations.find((org) => org.id === response.organizationId);
    const message = `Sent the invitation to ${response?.email}`;
    const description =
      response.actionResult === 'SentAgain' ? `Existing pending invitation to organization ${org?.name} found and was resent.` : '';

    if (invite === null || invite.organizationId === response.organizationId) {
      this._notifications_store.addToast(
        'Success',
        message,
        'success'
      );

      return;
    }
    this._notifications_store.addToast(
      'Warning',
      message,
      'info'
    );
  }
}
